/**
 * Bestsellers carousel — scroll-snap track with arrows, drag and optional auto-advance.
 * Used by `sections/bestsellers-carousel.liquid`.
 */
class BestsellersCarousel extends HTMLElement {
  /** @type {HTMLElement | null} */
  #track = null;

  /** @type {HTMLElement[]} */
  #slides = [];

  /** @type {HTMLButtonElement | null} */
  #previousButton = null;

  /** @type {HTMLButtonElement | null} */
  #nextButton = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {IntersectionObserver | null} */
  #revealObserver = null;

  /** @type {number | null} */
  #autoplayTimer = null;

  /** @type {number} */
  #autoplayInterval = 0;

  /** @type {number | null} */
  #dragStartX = null;

  /** @type {number} */
  #dragStartScroll = 0;

  /** @type {boolean} */
  #dragMoved = false;

  /** @type {number | null} */
  #scrollFrame = null;

  connectedCallback() {
    this.#track = this.querySelector('[data-bestsellers-track]');
    this.#slides = Array.from(this.querySelectorAll('[data-bestsellers-slide]'));

    const previousButton = this.querySelector('[data-bestsellers-previous]');
    const nextButton = this.querySelector('[data-bestsellers-next]');
    this.#previousButton = previousButton instanceof HTMLButtonElement ? previousButton : null;
    this.#nextButton = nextButton instanceof HTMLButtonElement ? nextButton : null;

    if (!this.#track) return;

    this.#previousButton?.addEventListener('click', this.#showPrevious);
    this.#nextButton?.addEventListener('click', this.#showNext);
    this.#track.addEventListener('scroll', this.#handleScroll, { passive: true });
    this.#track.addEventListener('pointerdown', this.#handlePointerDown);
    this.#track.addEventListener('pointermove', this.#handlePointerMove);
    this.#track.addEventListener('pointerup', this.#handlePointerUp);
    this.#track.addEventListener('pointercancel', this.#handlePointerUp);
    this.#track.addEventListener('click', this.#handleClick, { capture: true });
    this.addEventListener('keydown', this.#handleKeydown);
    document.addEventListener('shopify:block:select', this.#handleBlockSelect);

    this.#resizeObserver = new ResizeObserver(() => this.#update());
    this.#resizeObserver.observe(this.#track);

    this.#setupReveal();
    this.#setupAutoplay();
    this.#update();
  }

  disconnectedCallback() {
    this.#previousButton?.removeEventListener('click', this.#showPrevious);
    this.#nextButton?.removeEventListener('click', this.#showNext);
    this.#track?.removeEventListener('scroll', this.#handleScroll);
    this.#track?.removeEventListener('pointerdown', this.#handlePointerDown);
    this.#track?.removeEventListener('pointermove', this.#handlePointerMove);
    this.#track?.removeEventListener('pointerup', this.#handlePointerUp);
    this.#track?.removeEventListener('pointercancel', this.#handlePointerUp);
    this.#track?.removeEventListener('click', this.#handleClick, { capture: true });
    this.removeEventListener('keydown', this.#handleKeydown);
    document.removeEventListener('shopify:block:select', this.#handleBlockSelect);

    this.removeEventListener('pointerenter', this.#pauseAutoplay);
    this.removeEventListener('pointerleave', this.#resumeAutoplay);
    this.removeEventListener('focusin', this.#pauseAutoplay);
    this.removeEventListener('focusout', this.#resumeAutoplay);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);

    if (this.#scrollFrame !== null) cancelAnimationFrame(this.#scrollFrame);
    this.#scrollFrame = null;

    this.#resizeObserver?.disconnect();
    this.#revealObserver?.disconnect();
    this.#stopAutoplay();
  }

  #setupReveal() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.classList.add('bestsellers--visible');
      return;
    }

    this.classList.add('bestsellers--motion-ready');

    this.#revealObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this.classList.add('bestsellers--visible');
          this.#revealObserver?.unobserve(this);
        }
      },
      { threshold: 0.12 }
    );

    this.#revealObserver.observe(this);
  }

  #setupAutoplay() {
    const interval = Number(this.dataset.autoplay ?? 0);
    if (!interval || Number.isNaN(interval)) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (window.Shopify?.designMode) return;

    this.#autoplayInterval = interval;

    this.addEventListener('pointerenter', this.#pauseAutoplay);
    this.addEventListener('pointerleave', this.#resumeAutoplay);
    this.addEventListener('focusin', this.#pauseAutoplay);
    this.addEventListener('focusout', this.#resumeAutoplay);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#resumeAutoplay();
  }

  #resumeAutoplay = () => {
    if (!this.#autoplayInterval || this.#autoplayTimer !== null) return;

    this.#autoplayTimer = window.setInterval(() => {
      if (!this.#track) return;

      if (this.#maxScroll() - this.#track.scrollLeft <= 1) this.#goTo(0);
      else this.#goTo(this.#currentIndex() + 1);
    }, this.#autoplayInterval);
  };

  #pauseAutoplay = () => {
    if (this.#autoplayTimer === null) return;
    window.clearInterval(this.#autoplayTimer);
    this.#autoplayTimer = null;
  };

  #stopAutoplay() {
    this.#pauseAutoplay();
    this.#autoplayInterval = 0;
  }

  #handleVisibilityChange = () => {
    if (document.hidden) this.#pauseAutoplay();
    else this.#resumeAutoplay();
  };

  #showPrevious = () => {
    this.#goTo(this.#currentIndex() - 1);
  };

  #showNext = () => {
    this.#goTo(this.#currentIndex() + 1);
  };

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    if (event.target instanceof HTMLElement && event.target.closest('a, button')) return;

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.#showPrevious();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.#showNext();
    }
  };

  #handleScroll = () => {
    if (this.#scrollFrame !== null) return;

    this.#scrollFrame = requestAnimationFrame(() => {
      this.#scrollFrame = null;
      this.#update();
    });
  };

  /** @param {PointerEvent} event */
  #handlePointerDown = (event) => {
    if (event.pointerType !== 'mouse' || !this.#track) return;
    if (event.button !== 0) return;

    this.#dragStartX = event.clientX;
    this.#dragStartScroll = this.#track.scrollLeft;
    this.#dragMoved = false;
  };

  /** @param {PointerEvent} event */
  #handlePointerMove = (event) => {
    if (this.#dragStartX === null || !this.#track) return;

    const distance = event.clientX - this.#dragStartX;
    if (!this.#dragMoved && Math.abs(distance) < 6) return;

    if (!this.#dragMoved) {
      this.#dragMoved = true;
      this.classList.add('bestsellers--dragging');
      this.#pauseAutoplay();
      this.#track.setPointerCapture(event.pointerId);
    }

    this.#track.scrollLeft = this.#dragStartScroll - distance;
  };

  /** @param {PointerEvent} event */
  #handlePointerUp = (event) => {
    if (this.#dragStartX === null) return;

    this.#dragStartX = null;

    if (!this.#dragMoved) return;

    this.classList.remove('bestsellers--dragging');
    if (this.#track?.hasPointerCapture(event.pointerId)) this.#track.releasePointerCapture(event.pointerId);
    this.#goTo(this.#currentIndex());
    this.#resumeAutoplay();
  };

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    if (!this.#dragMoved) return;

    event.preventDefault();
    event.stopPropagation();
    this.#dragMoved = false;
  };

  /** @param {Event} event */
  #handleBlockSelect = (event) => {
    const block = event.target;
    if (!(block instanceof HTMLElement) || !this.contains(block)) return;

    const index = this.#slides.indexOf(block);
    if (index < 0) return;

    this.#pauseAutoplay();
    this.#goTo(index);
  };

  /** Distance between the start of two adjacent slides. */
  #step() {
    const first = this.#slides[0];
    const second = this.#slides[1];
    if (!first) return 0;
    if (second) return second.offsetLeft - first.offsetLeft;
    return first.offsetWidth;
  }

  #maxScroll() {
    if (!this.#track) return 0;
    return Math.max(0, this.#track.scrollWidth - this.#track.clientWidth);
  }

  #currentIndex() {
    const step = this.#step();
    if (!this.#track || step <= 0) return 0;

    return Math.max(0, Math.min(this.#slides.length - 1, Math.round(this.#track.scrollLeft / step)));
  }

  /** @param {number} index */
  #goTo(index) {
    const first = this.#slides[0];
    if (!this.#track || !first) return;

    const target = this.#slides[Math.max(0, Math.min(this.#slides.length - 1, index))];
    if (!target) return;

    this.#track.scrollTo({
      left: Math.min(target.offsetLeft - first.offsetLeft, this.#maxScroll()),
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  }

  #update() {
    if (!this.#track) return;

    const maxScroll = this.#maxScroll();
    const scrollLeft = this.#track.scrollLeft;
    const scrollable = maxScroll > 1;

    if (this.#previousButton) this.#previousButton.disabled = !scrollable || scrollLeft <= 1;
    if (this.#nextButton) this.#nextButton.disabled = !scrollable || scrollLeft >= maxScroll - 1;
  }
}

if (!customElements.get('bestsellers-carousel')) {
  customElements.define('bestsellers-carousel', BestsellersCarousel);
}
